import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import {
  getJSAEvaluationsList,
  saveJSAEvaluationRow,
  deleteJSAEvaluationRow,
  getActivitiesList,
  type JSAEvaluationRow,
  type JSAAdequacyRating,
  type JSAProcessAdherence,
  type ActivityRow,
} from '@/services/db/dsrEntry';
import PhotoUploader from '@/components/dsr/PhotoUploader';
import { FieldLabel, FieldError, ChipGroup } from './_shared';
import CreateActionItemButton from './CreateActionItemButton';

const ADEQUACY_OPTIONS: { value: JSAAdequacyRating; label: string; color: string }[] = [
  { value: 'adequate', label: 'Adequate', color: '#3ba870' },
  { value: 'adequate_with_comments', label: 'Adequate with Comments', color: '#00b4b4' },
  { value: 'inadequate', label: 'Inadequate', color: '#d87820' },
];

const ADHERENCE_OPTIONS: { value: JSAProcessAdherence; label: string; color: string }[] = [
  { value: 'following_jsa', label: 'Following JSA', color: '#3ba870' },
  { value: 'minor_deviations', label: 'Minor Deviations', color: '#00b4b4' },
  { value: 'not_following_jsa', label: 'Not Following JSA', color: '#d87820' },
];

const entrySchema = z
  .object({
    jsa_reference: z.string().min(1, 'JSA reference is required').max(200),
    activity_entry_id: z.string().optional(),
    activity_description: z.string().max(300).optional(),
    work_crew: z.string().optional(),
    adequacy_rating: z.string().min(1, 'Adequacy rating is required'),
    hazards_identified_count: z.string().optional(),
    controls_in_place: z.string().max(500).optional(),
    process_adherence: z.string().min(1, 'Process adherence is required'),
    corrective_action: z.string().max(500).optional(),
    observer_name: z.string().optional(),
    comments: z.string().max(300).optional(),
  })
  .refine(
    (data) =>
      data.process_adherence !== 'not_following_jsa' ||
      (data.corrective_action && data.corrective_action.trim().length > 0),
    {
      message: 'Corrective action is required when not following JSA',
      path: ['corrective_action'],
    },
  );

type EntryFormValues = z.infer<typeof entrySchema>;

const EMPTY: EntryFormValues = {
  jsa_reference: '',
  activity_entry_id: '',
  activity_description: '',
  work_crew: '',
  adequacy_rating: 'adequate',
  hazards_identified_count: '',
  controls_in_place: '',
  process_adherence: 'following_jsa',
  corrective_action: '',
  observer_name: '',
  comments: '',
};

interface Props {
  dsrId: string;
  isReadOnly?: boolean;
}

export default function JSAEvaluationsForm({ dsrId, isReadOnly }: Props) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'closed' | 'adding' | { editing: JSAEvaluationRow }>(
    'closed',
  );
  const [prefill, setPrefill] = useState<{ activityId: string; jsaRef: string } | null>(null);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['entry_jsa_evaluations', dsrId],
    queryFn: () => getJSAEvaluationsList(dsrId),
    staleTime: 30 * 1000,
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['entry_activities', dsrId],
    queryFn: () => getActivitiesList(dsrId),
    staleTime: 30 * 1000,
  });

  const jsaRequiredActivities = activities.filter((a) => a.is_jsa_required);
  const reviewedActivityIds = new Set(
    entries.filter((e) => e.activity_entry_id).map((e) => e.activity_entry_id),
  );

  const totalEvaluations = entries.length;
  const inadequateCount = entries.filter((e) => e.adequacy_rating === 'inadequate').length;
  const notFollowingCount = entries.filter(
    (e) => e.process_adherence === 'not_following_jsa',
  ).length;

  const onSaved = () => {
    setMode('closed');
    setPrefill(null);
    queryClient.invalidateQueries({ queryKey: ['entry_jsa_evaluations', dsrId] });
    queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
  };

  const startFromActivity = (a: ActivityRow) => {
    const date = new Date().toISOString().slice(0, 10);
    const shortName = a.description.slice(0, 30).replace(/\s+/g, '-');
    setPrefill({ activityId: a.id, jsaRef: `JSA-${date}-${shortName}` });
    setMode('adding');
  };

  const onDelete = async (id: string) => {
    if (!confirm('Remove this JSA evaluation?')) return;
    try {
      await deleteJSAEvaluationRow(id);
      queryClient.invalidateQueries({ queryKey: ['entry_jsa_evaluations', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete evaluation');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">JSA Evaluations</h2>
        <p className="text-xs text-muted-foreground">
          Job Safety Analysis reviews and process-adherence evaluations.
        </p>
      </div>

      {entries.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-3 gap-4">
              <Stat value={totalEvaluations} label="Evaluations" />
              <Stat value={inadequateCount} label="Inadequate" warn={inadequateCount > 0} />
              <Stat value={notFollowingCount} label="Not Following" warn={notFollowingCount > 0} />
            </div>
          </CardContent>
        </Card>
      )}

      {jsaRequiredActivities.length > 0 && (
        <Card className="border-mozaic-orange/40 bg-mozaic-orange/5">
          <CardContent className="pt-6">
            <h3 className="mb-1 text-sm font-semibold text-mozaic-navy">
              Activities flagged for JSA review
            </h3>
            <p className="mb-3 text-xs text-muted-foreground">
              {jsaRequiredActivities.length === 0
                ? 'No activities flagged for JSA this shift.'
                : 'Tap "Create JSA Evaluation" to start an evaluation pre-filled from the activity.'}
            </p>
            <div className="space-y-2">
              {jsaRequiredActivities.map((a) => {
                const reviewed = reviewedActivityIds.has(a.id);
                const riskColor =
                  a.risk_level === 'high'
                    ? 'bg-mozaic-red'
                    : a.risk_level === 'medium'
                      ? 'bg-mozaic-orange'
                      : 'bg-mozaic-green';
                return (
                  <div
                    key={a.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded border border-mozaic-orange/20 bg-background p-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex flex-wrap items-center gap-1">
                        <Badge className={`${riskColor} text-[10px] text-white`}>
                          {a.risk_level.toUpperCase()}
                        </Badge>
                        {(a.risk_tags ?? []).slice(0, 3).map((t) => (
                          <Badge
                            key={t}
                            variant="outline"
                            className="text-[10px]"
                          >
                            {t}
                          </Badge>
                        ))}
                      </div>
                      <p className="truncate text-sm">{a.description}</p>
                    </div>
                    {reviewed ? (
                      <Badge className="bg-mozaic-green text-[10px] text-white">
                        REVIEWED
                      </Badge>
                    ) : (
                      !isReadOnly && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-mozaic-teal text-mozaic-teal hover:bg-mozaic-teal/10"
                          onClick={() => startFromActivity(a)}
                        >
                          Create JSA Evaluation →
                        </Button>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {entries.length === 0 && mode === 'closed' && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No JSA evaluations yet. Add one below.
          </CardContent>
        </Card>
      )}

      {entries.map((entry) => (
        <JSACard
          key={entry.id}
          entry={entry}
          dsrId={dsrId}
          onEdit={() => setMode({ editing: entry })}
          onDelete={() => onDelete(entry.id)}
          isReadOnly={isReadOnly}
        />
      ))}

      {!isReadOnly && mode !== 'closed' && tenantId && (
        <JSAEntryForm
          dsrId={dsrId}
          tenantId={tenantId}
          activities={activities}
          editing={mode !== 'adding' ? mode.editing : undefined}
          prefill={mode === 'adding' ? prefill : null}
          onSaved={onSaved}
          onCancel={() => {
            setMode('closed');
            setPrefill(null);
          }}
        />
      )}

      {!isReadOnly && mode === 'closed' && (
        <Button
          variant="outline"
          className="w-full border-dashed border-mozaic-teal text-mozaic-teal"
          onClick={() => {
            setPrefill(null);
            setMode('adding');
          }}
        >
          <Plus className="mr-1 h-4 w-4" /> Add JSA Evaluation
        </Button>
      )}
    </div>
  );
}

function Stat({ value, label, warn }: { value: number; label: string; warn?: boolean }) {
  return (
    <div className="text-center">
      <div className={`text-2xl font-bold ${warn ? 'text-mozaic-orange' : 'text-mozaic-teal'}`}>
        {value}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function JSACard({
  entry,
  dsrId,
  onEdit,
  onDelete,
  isReadOnly,
}: {
  entry: JSAEvaluationRow;
  dsrId: string;
  onEdit: () => void;
  onDelete: () => void;
  isReadOnly?: boolean;
}) {
  const adequacy = ADEQUACY_OPTIONS.find((o) => o.value === entry.adequacy_rating);
  const adherence = ADHERENCE_OPTIONS.find((o) => o.value === entry.process_adherence);
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-2 flex flex-wrap gap-2">
          {adequacy && (
            <Badge style={{ backgroundColor: adequacy.color }} className="text-white">
              {adequacy.label.toUpperCase()}
            </Badge>
          )}
          {adherence && (
            <Badge style={{ backgroundColor: adherence.color }} className="text-white">
              {adherence.label.toUpperCase()}
            </Badge>
          )}
        </div>
        <h3 className="font-semibold text-mozaic-navy">{entry.jsa_reference}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {entry.hazards_identified_count != null
            ? `${entry.hazards_identified_count} hazard${
                entry.hazards_identified_count === 1 ? '' : 's'
              } identified`
            : ''}
          {entry.hazards_identified_count != null && entry.work_crew ? ' · ' : ''}
          {entry.work_crew ?? ''}
        </p>
        <PhotoUploader
          entityType="jsa_evaluation"
          entityId={entry.id}
          dsrId={dsrId}
          readOnly={isReadOnly}
        />
        {!isReadOnly && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
            <Button variant="ghost" size="sm" className="text-mozaic-teal" onClick={onEdit}>
              <Pencil className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
            {entry.adequacy_rating === 'inadequate' && (
              <div className="ml-auto">
                <CreateActionItemButton
                  dsrId={dsrId}
                  sourceSection="jsa_evaluations"
                  defaultDescription={`JSA deficiency: ${entry.jsa_reference}`}
                  defaultPriority="high"
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function JSAEntryForm({
  dsrId,
  tenantId,
  activities,
  editing,
  prefill,
  onSaved,
  onCancel,
}: {
  dsrId: string;
  tenantId: string;
  activities: ActivityRow[];
  editing?: JSAEvaluationRow;
  prefill?: { activityId: string; jsaRef: string } | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const hasActivities = activities.length > 0;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    defaultValues: editing
      ? {
          jsa_reference: editing.jsa_reference,
          activity_entry_id: editing.activity_entry_id ?? '',
          activity_description: editing.activity_description ?? '',
          work_crew: editing.work_crew ?? '',
          adequacy_rating: editing.adequacy_rating,
          hazards_identified_count: editing.hazards_identified_count?.toString() ?? '',
          controls_in_place: editing.controls_in_place ?? '',
          process_adherence: editing.process_adherence,
          corrective_action: editing.corrective_action ?? '',
          observer_name: editing.observer_name ?? '',
          comments: editing.comments ?? '',
        }
      : prefill
        ? { ...EMPTY, activity_entry_id: prefill.activityId, jsa_reference: prefill.jsaRef }
        : EMPTY,
  });

  const adherence = watch('process_adherence');

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      await saveJSAEvaluationRow({
        id: editing?.id,
        dsrReportId: dsrId,
        tenantId,
        jsaReference: values.jsa_reference,
        activityEntryId:
          hasActivities && values.activity_entry_id ? values.activity_entry_id : null,
        activityDescription:
          !hasActivities && values.activity_description ? values.activity_description : null,
        workCrew: values.work_crew || null,
        adequacyRating: values.adequacy_rating as JSAAdequacyRating,
        hazardsIdentifiedCount: values.hazards_identified_count
          ? parseInt(values.hazards_identified_count, 10)
          : null,
        controlsInPlace: values.controls_in_place || null,
        processAdherence: values.process_adherence as JSAProcessAdherence,
        correctiveAction: values.corrective_action || null,
        observerName: values.observer_name || null,
        comments: values.comments || null,
      });
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save evaluation');
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="border-2 border-mozaic-teal">
      <CardContent className="space-y-4 pt-6">
        <h3 className="font-semibold">
          {editing ? 'Edit JSA Evaluation' : 'New JSA Evaluation'}
        </h3>

        <div className="space-y-2">
          <FieldLabel required>JSA Reference / Title</FieldLabel>
          <Controller
            control={control}
            name="jsa_reference"
            render={({ field }) => (
              <Input
                {...field}
                maxLength={200}
                placeholder="e.g. JSA-2026-001 or Crane lifting operations"
              />
            )}
          />
          <FieldError message={errors.jsa_reference?.message} />
        </div>

        {hasActivities ? (
          <div className="space-y-2">
            <FieldLabel>Associated Activity</FieldLabel>
            <Controller
              control={control}
              name="activity_entry_id"
              render={({ field }) => (
                <ChipGroup
                  options={[
                    { value: '', label: 'None' },
                    ...activities.map((a) => ({
                      value: a.id,
                      label:
                        a.description.length > 30
                          ? a.description.slice(0, 30) + '…'
                          : a.description,
                    })),
                  ]}
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(v)}
                  wrap
                />
              )}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <FieldLabel>Associated Activity</FieldLabel>
            <Controller
              control={control}
              name="activity_description"
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  maxLength={300}
                  placeholder="Describe the associated activity"
                />
              )}
            />
          </div>
        )}

        <div className="space-y-2">
          <FieldLabel>Work Crew / Contractor</FieldLabel>
          <Controller
            control={control}
            name="work_crew"
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                placeholder="e.g. Night shift crew, ABC Contractors"
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <FieldLabel required>JSA Adequacy Rating</FieldLabel>
          <Controller
            control={control}
            name="adequacy_rating"
            render={({ field }) => (
              <ChipGroup
                options={ADEQUACY_OPTIONS}
                value={field.value as JSAAdequacyRating}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.adequacy_rating?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel>Hazards Identified</FieldLabel>
          <Controller
            control={control}
            name="hazards_identified_count"
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                type="number"
                inputMode="numeric"
                min={0}
                className="w-32"
                placeholder="0"
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <FieldLabel>Controls in Place</FieldLabel>
          <Controller
            control={control}
            name="controls_in_place"
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ''}
                rows={3}
                maxLength={500}
                placeholder="Describe controls and mitigations in place"
              />
            )}
          />
          <FieldError message={errors.controls_in_place?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Process Adherence</FieldLabel>
          <Controller
            control={control}
            name="process_adherence"
            render={({ field }) => (
              <ChipGroup
                options={ADHERENCE_OPTIONS}
                value={field.value as JSAProcessAdherence}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.process_adherence?.message} />
        </div>

        {adherence === 'not_following_jsa' && (
          <>
            <div className="rounded-md border border-mozaic-orange/40 bg-mozaic-orange/10 px-3 py-2">
              <p className="text-xs font-semibold text-mozaic-orange">
                Consider documenting a Stop Work Authority event for this deviation.
              </p>
            </div>
            <div className="space-y-2">
              <FieldLabel required>Corrective Action</FieldLabel>
              <Controller
                control={control}
                name="corrective_action"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    value={field.value ?? ''}
                    rows={3}
                    maxLength={500}
                    placeholder="Describe the corrective action taken"
                  />
                )}
              />
              <FieldError message={errors.corrective_action?.message} />
            </div>
          </>
        )}

        <div className="space-y-2">
          <FieldLabel>Observer Name</FieldLabel>
          <Controller
            control={control}
            name="observer_name"
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                placeholder="Name of the JSA observer"
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <FieldLabel>Comments</FieldLabel>
          <Controller
            control={control}
            name="comments"
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                maxLength={300}
                placeholder="Additional notes"
              />
            )}
          />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : editing ? (
              'Update Evaluation'
            ) : (
              'Save Evaluation'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
